import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { api } from '@/lib/api';
import {
  Card,
  ErrorState,
  Loading,
  PageHeader,
  Select,
  StatCard,
} from '@/components/ui';
import {
  WINDOWS,
  formatDate,
  formatMoney,
  formatNumber,
  formatPercent,
} from '@/lib/format';
import { SERIES, axisProps, gridProps, tooltipProps } from '@/lib/chartTheme';

interface RevenueResponse {
  window_days: number;
  actual_cost: number;
  client_price: number;
  gross_margin: number;
  markup_pct: number | null;
  margin_pct: number | null;
  calls: number;
  minutes: number;
  qualified_leads: number;
  transfers: number;
  cost_per_call: number;
  cost_per_minute: number;
  cost_per_qualified_lead: number;
  revenue_per_qualified_lead: number;
  by_day: Array<{
    day: string;
    actual_cost: string;
    client_price: string;
    gross_margin: string;
  }>;
  by_client: Array<{
    tenant_id: string;
    name: string;
    price_multiplier: string;
    billing_mode?: string;
    licence_revenue?: number;
    usage_revenue?: number;
    actual_cost: string;
    client_price: string;
    gross_margin: string;
    calls: number;
  }>;
}

export default function Revenue() {
  const [days, setDays] = useState(30);

  const { data, isPending, error, refetch } = useQuery({
    queryKey: ['admin-revenue', days],
    queryFn: () => api.get<RevenueResponse>('/admin/revenue', { days }),
  });

  if (isPending) return <Loading />;
  if (error) return <ErrorState error={error} retry={() => refetch()} />;

  const negativeMargin = data.by_client.filter((c) => Number(c.gross_margin) < 0);

  return (
    <div className="w-full">
      <PageHeader
        title="Revenue & margin"
        description="What clients are charged, what providers cost, and what is left."
        actions={
          <Select
            value={days}
            onChange={(e) => setDays(Number(e.target.value))}
            className="w-auto"
          >
            {WINDOWS.map((w) => (
              <option key={w.days} value={w.days}>
                Last {w.label}
              </option>
            ))}
          </Select>
        }
      />

      {negativeMargin.length > 0 && (
        <div className="mb-4 rounded-md bg-red-50 px-4 py-3 text-sm text-red-800 ring-1 ring-inset ring-red-200 dark:bg-red-950/40 dark:text-red-300 dark:ring-red-900">
          <strong>{negativeMargin.length}</strong> client
          {negativeMargin.length === 1 ? ' is' : 's are'} running at a negative margin:{' '}
          {negativeMargin.map((c) => c.name).join(', ')}. Check their multiplier and the
          provider rates.
        </div>
      )}

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <StatCard label="Client revenue" value={formatMoney(data.client_price)} />
        <StatCard label="Provider cost" value={formatMoney(data.actual_cost)} />
        <StatCard
          label="Gross margin"
          value={formatMoney(data.gross_margin)}
          sub={data.margin_pct !== null ? `${formatPercent(data.margin_pct)} margin` : undefined}
          tone={data.gross_margin >= 0 ? 'good' : 'bad'}
        />
        <StatCard
          label="Markup"
          value={data.markup_pct !== null ? formatPercent(data.markup_pct, 0) : '—'}
          sub="over provider cost"
        />
      </div>

      <div className="mt-4 grid grid-cols-2 gap-4 lg:grid-cols-4">
        <StatCard label="Cost per call" value={formatMoney(data.cost_per_call)} />
        <StatCard label="Cost per minute" value={formatMoney(data.cost_per_minute)} />
        <StatCard
          label="Cost per qualified lead"
          value={formatMoney(data.cost_per_qualified_lead)}
        />
        <StatCard
          label="Revenue per qualified lead"
          value={formatMoney(data.revenue_per_qualified_lead)}
          tone="good"
        />
      </div>

      <Card title="Cost vs revenue" className="mt-6">
        {data.by_day.length === 0 ? (
          <p className="py-12 text-center text-sm text-ink-500">No activity in this window.</p>
        ) : (
          <ResponsiveContainer width="100%" height={280}>
            <AreaChart
              data={data.by_day.map((row) => ({
                day: row.day,
                cost: Number(row.actual_cost),
                revenue: Number(row.client_price),
                margin: Number(row.gross_margin),
              }))}
              margin={{ top: 8, right: 8, left: -12, bottom: 0 }}
            >
              <CartesianGrid {...gridProps} />
              <XAxis
                dataKey="day"
                tickFormatter={formatDate}
                {...axisProps}
              />
              <YAxis {...axisProps} />
              <Tooltip
                labelFormatter={formatDate}
                formatter={(value: number) => formatMoney(value)}
                {...tooltipProps}
              />
              <Area
                type="monotone"
                dataKey="revenue"
                name="Client revenue"
                stroke={SERIES.primary}
                fill={SERIES.primary}
                fillOpacity={0.15}
                strokeWidth={2}
              />
              <Area
                type="monotone"
                dataKey="cost"
                name="Provider cost"
                stroke={SERIES.danger}
                fill={SERIES.danger}
                fillOpacity={0.12}
                strokeWidth={2}
              />
              <Area
                type="monotone"
                dataKey="margin"
                name="Gross margin"
                stroke={SERIES.success}
                fill={SERIES.success}
                fillOpacity={0.1}
                strokeWidth={2}
              />
            </AreaChart>
          </ResponsiveContainer>
        )}
      </Card>

      <Card title="By client" className="mt-4" padded={false}>
        <div className="overflow-x-auto">
          <table className="glee-table">
            <thead>
              <tr>
                {['Client', 'Billing', 'Calls', 'Provider cost', 'Revenue', 'Profit', 'Margin %'].map(
                  (header, index) => (
                    <th
                      key={header}
                      className={`${
                        index >= 1 ? 'text-right' : 'text-left'
                      }`}
                    >
                      {header}
                    </th>
                  ),
                )}
              </tr>
            </thead>
            <tbody>
              {data.by_client.map((row) => {
                const cost = Number(row.actual_cost);
                const revenue = Number(row.client_price);
                const margin = Number(row.gross_margin);
                const pct = revenue > 0 ? (1 - cost / revenue) * 100 : null;
                return (
                  <tr key={row.tenant_id}>
                    <td>
                      <Link
                        to={`/clients/${row.tenant_id}`}
                        className="font-medium text-brand-700 hover:underline dark:text-brand-400"
                      >
                        {row.name}
                      </Link>
                    </td>
                    <td className="text-right text-xs">
                      {row.billing_mode === 'MONTHLY'
                        ? 'Monthly licence'
                        : row.billing_mode === 'MONTHLY_PLUS_USAGE'
                          ? 'Licence + usage'
                          : `Per minute · ${Number(row.price_multiplier).toFixed(2)}×`}
                    </td>
                    <td className="text-right tabular-nums">
                      {formatNumber(row.calls)}
                    </td>
                    <td className="text-right tabular-nums">{formatMoney(cost)}</td>
                    <td className="text-right tabular-nums">{formatMoney(revenue)}</td>
                    <td
                      className={`text-right tabular-nums ${
                        margin < 0 ? 'text-red-600' : 'text-emerald-600'
                      }`}
                    >
                      {formatMoney(margin)}
                    </td>
                    <td className="text-right tabular-nums">
                      {pct === null ? '—' : formatPercent(pct)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
